import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useUser } from '@/contexts/UserContext';
import { motion, AnimatePresence } from 'framer-motion';

const UpdateAddressModal = ({ isOpen, setIsOpen, fetchAddress, handleSubmit }) => {
  const { user, refreshUser } = useUser();
  const [address, setAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const addressRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addressRef.current && !addressRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateAddress = async () => {
    if (!user?.addressModel?.at(0)) {
      toast.error('Please add your address in profile to proceed');
      return;
    }

    if (!address?.unitNo) {
      toast.error('Please enter a valid flat/house number');
      return;
    }

    const apiData = {
      ...user?.addressModel?.at(0),
      ...address,
    };

    setLoading(true);

    try {
      await axios.post(`${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/address`, apiData, {
        headers: {
          'X-User-Id': user?.id,
          sessionToken: localStorage.getItem('sessionToken'),
          refreshToken: localStorage.getItem('refreshToken'),
        },
      });

      refreshUser(user?.contactNo);
      fetchAddress();
      setIsOpen(false);
      handleSubmit(apiData?.unitNo);
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Something went wrong! Please try again!');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-xl shadow-xl max-w-md w-11/12 mx-auto"
          ref={addressRef}
        >
          <div className="p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Update Address</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Flat/House No</label>
              <input
                type="text"
                value={address?.unitNo || ''}
                onChange={(e) => setAddress((prev) => ({ ...prev, unitNo: e.target.value }))}
                className="py-2.5 px-3 focus:outline-none border border-gray-300 focus:border-green-500 rounded-lg w-full"
                placeholder="Enter flat/house number"
              />
            </div>

            <div className="flex justify-between gap-3 mt-5">
              <button
                onClick={() => setIsOpen(false)}
                className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg flex-1 font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={updateAddress}
                disabled={loading}
                className="px-4 py-2.5 bg-green-500 text-white rounded-lg flex-1 font-medium hover:bg-green-600 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update & Place Order'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default UpdateAddressModal;
